import { motion } from "framer-motion";
import { useTab } from "@/hooks/useTab";
import { useFilters } from "@/hooks/useFilters";
import { Gauge, type BurnBand } from "@/components/cards/Gauge";
import { BurnRow } from "@/components/cards/BurnRow";
import { BurnStatsRow } from "@/components/cards/BurnStatsRow";
import { fmt } from "@/lib/format";
import { EmptyState, ErrorState, SectionTitle, TabSkeleton } from "./shared";

function pctToBand(value: number): BurnBand {
  if (value >= 100) return "over";
  if (value >= 75) return "warn";
  return "safe";
}

const BAND_COPY: Record<BurnBand, { label: string; tone: string; dot: string }> = {
  safe: {
    label: "Hay margen — aprovéchalo y pídele más a Kiro",
    tone: "text-brand-1",
    dot: "bg-brand-1 shadow-[0_0_14px_rgba(167,139,250,0.85)]",
  },
  warn: {
    label: "Buen ritmo — seguid exprimiendo el plan",
    tone: "text-amber-300",
    dot: "bg-amber-400 shadow-[0_0_14px_rgba(251,191,36,0.85)]",
  },
  over: {
    label: "A tope — plan exprimido al máximo",
    tone: "text-rose-300",
    dot: "bg-rose-500 shadow-[0_0_16px_rgba(239,68,68,0.95)]",
  },
};

interface HeroKpiProps {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
}

function HeroKpi({ label, value, hint, accent = "#a78bfa" }: HeroKpiProps) {
  return (
    <div
      className="relative overflow-hidden rounded-[18px] border border-border-1 bg-white/[0.03] px-5 py-4 backdrop-blur-sm"
      style={{ boxShadow: `inset 0 1px 0 rgba(255,255,255,0.06), 0 0 40px -20px ${accent}` }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl"
        style={{ background: accent, opacity: 0.15 }}
      />
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-text-4">
        {label}
      </div>
      <div className="tabular mt-1 text-3xl font-bold font-display text-text-1">
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-text-3">{hint}</div>}
    </div>
  );
}

/** Tab 6 — Credits burn % of each user's plan cap. */
export function BurnTab() {
  const { filters } = useFilters();
  const { data, isPending, error } = useTab("burn", filters);

  if (isPending) return <TabSkeleton />;
  if (error) return <ErrorState error={error} />;
  if (!data || data.rows.length === 0) {
    return <EmptyState title="Sin datos de consumo." />;
  }

  const overallBand = pctToBand(data.overall.pct);
  const { kpis } = data;
  const bandCopy = BAND_COPY[overallBand];

  const projected =
    data.rows.length > 0
      ? data.rows.reduce((acc, r) => acc + (r.projected_month_end ?? r.pct), 0) /
        data.rows.length
      : undefined;

  return (
    <div className="flex flex-col gap-8">
      <section
        aria-label="Consumo global"
        className="relative overflow-hidden rounded-[24px] border border-border-2 bg-bg-1/60 p-8 backdrop-blur-xl"
        style={{
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.08), 0 30px 80px -40px rgba(167,139,250,0.45)",
        }}
      >
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 motion-reduce:hidden"
          style={{
            background:
              "radial-gradient(600px 300px at 15% 10%, rgba(167,139,250,0.28), transparent 60%), radial-gradient(500px 260px at 85% 0%, rgba(236,72,153,0.22), transparent 60%), radial-gradient(400px 240px at 50% 110%, rgba(251,191,36,0.14), transparent 65%)",
          }}
          animate={{ opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(167,139,250,0.8), rgba(236,72,153,0.8), transparent)",
          }}
        />

        <div className="relative grid grid-cols-[auto_1fr] items-center gap-10">
          <div className="relative">
            <div
              aria-hidden
              className="absolute inset-0 -z-10 rounded-full blur-3xl"
              style={{ background: "rgba(167,139,250,0.25)" }}
            />
            <Gauge
              value={data.overall.pct}
              band={overallBand}
              label={`uso ${data.overall.plan}`}
              size={220}
              projected={projected}
            />
          </div>

          <div className="flex flex-col gap-5">
            <div>
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-text-4">
                <span className="inline-block h-1 w-6 rounded-full bg-gradient-to-r from-brand-1 to-brand-3" />
                Quema de plan
              </div>
              <h2 className="mt-2 text-2xl font-bold font-display text-text-1">
                Consumo del plan en tiempo real
              </h2>
              <div className="mt-1 text-sm text-text-3">
                {fmt(data.overall.used)}{" "}
                <span className="text-text-4">/ {fmt(data.overall.cap)} créditos</span>
              </div>
            </div>

            <div
              className={`inline-flex w-fit items-center gap-3 rounded-full border border-border-2 bg-white/[0.04] px-4 py-2 text-sm font-medium ${bandCopy.tone}`}
            >
              <motion.span
                className={`inline-block h-2.5 w-2.5 rounded-full ${bandCopy.dot}`}
                animate={{ opacity: [1, 0.35, 1], scale: [1, 1.25, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              />
              <span className="text-text-2 normal-case tracking-normal">
                {bandCopy.label}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <HeroKpi
                label="Usuarios activos"
                value={fmt(kpis.active_users)}
                accent="#a78bfa"
              />
              <HeroKpi
                label="Al 95%+"
                value={fmt(kpis.users_over_95)}
                hint="en zona crítica"
                accent="#ec4899"
              />
              <HeroKpi
                label="Ritmo diario"
                value={fmt(kpis.daily_burn_rate)}
                hint="créditos/día"
                accent="#fbbf24"
              />
            </div>
          </div>
        </div>
      </section>

      <BurnStatsRow overall={data.overall} kpis={kpis} rows={data.rows} />

      <section>
        <SectionTitle>Consumo por usuario</SectionTitle>
        <div className="flex flex-col gap-2">
          {data.rows.map((row, i) => (
            <BurnRow key={row.uid} row={row} rank={i + 1} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
